/**
 * The shapes a new scenario, package, line item or year starts life as, plus the
 * per-basis metadata the editor needs to label a line item honestly.
 *
 * Everything here is a factory rather than a shared constant: the editor mutates
 * drafts, and a module-level object handed out twice would alias.
 */

import {
  DEFAULT_COMMITMENT_YEARS,
  DEFAULT_HORIZON_YEARS,
  MAX_HORIZON_YEARS,
  MIN_HORIZON_YEARS,
  PACKAGE_COLOR_SLOTS,
} from "../constants.js";
import type {
  BillingModel,
  ChurnMode,
  LineBasis,
  LineDirection,
  LineGroup,
  LineItemInput,
  MaintenanceMode,
  PackageInput,
  ScenarioModel,
  YearInput,
} from "./types.js";

export const DEFAULT_CURRENCY_CODE = "USD";

/** Names for the first few packages, so an empty scenario is not "Package 1". */
const STARTER_PACKAGE_NAMES = ["Starter", "Pro", "Enterprise"] as const;

/**
 * Deterministic ids. The engine only requires ids to be unique within a model,
 * and a pure module must not invent randomness; the server replaces these with
 * cuids when the draft is first persisted, and the collection diff in
 * `PUT /api/scenarios/:id/model` keys on whatever id came back.
 */
export const draftPackageId = (index: number): string => `pkg-${index + 1}`;
export const draftLineItemId = (index: number): string => `li-${index + 1}`;

export function blankPackage(index = 0): PackageInput {
  return {
    id: draftPackageId(index),
    name: STARTER_PACKAGE_NAMES[index] ?? `Package ${index + 1}`,
    sortOrder: index,
    colorIndex: index % PACKAGE_COLOR_SLOTS,
    billingModel: "SUBSCRIPTION",
    recurringPrice: 0,
    recurringCost: 0,
    recurringPeriod: "ANNUAL",
    oneTimePrice: 0,
    oneTimeCost: 0,
    assetCost: 0,
    maintenanceMode: "NONE",
    maintenanceRatePct: 0,
    maintenanceFlatAmount: 0,
    // Year 1 is usually bundled into the licence, so support starts in year 2.
    maintenanceStartYear: 2,
    maintenanceAttachPct: 100,
  };
}

export function blankLineItem(index = 0): LineItemInput {
  return {
    id: draftLineItemId(index),
    name: "",
    direction: "COST",
    group: "OTHER",
    basis: "FIXED_PER_YEAR",
    amount: 0,
    unitsPerClient: 1,
    attachPct: 100,
    startYear: 1,
    endYear: null,
    packageIds: [],
    sortOrder: index,
  };
}

/**
 * A year nobody signs up in. `packageIds` seeds the mix with an explicit zero
 * per package: the engine reads a missing key as zero either way, but a
 * complete mix is the canonical shape, so a normalise or a save round trip does
 * not come back looking like an edit.
 */
export function blankYear(packageIds: readonly string[] = []): YearInput {
  const mix: Record<string, number> = {};
  for (const id of packageIds) mix[id] = 0;
  return { newClients: 0, mix, churnMode: "COUNT", churnCount: 0, churnRatePct: 0 };
}

/**
 * A scenario with one empty package and a flat horizon — what "New scenario"
 * produces when there is no baseline to fork.
 */
export function blankScenarioModel(
  overrides: Partial<Pick<ScenarioModel, "currencyCode" | "horizonYears" | "commitmentYears">> = {},
): ScenarioModel {
  const horizonYears = Math.min(
    MAX_HORIZON_YEARS,
    Math.max(MIN_HORIZON_YEARS, overrides.horizonYears ?? DEFAULT_HORIZON_YEARS),
  );

  const packages = [blankPackage(0)];
  const packageIds = packages.map((pkg) => pkg.id);

  return {
    currencyCode: overrides.currencyCode ?? DEFAULT_CURRENCY_CODE,
    horizonYears,
    commitmentYears: overrides.commitmentYears ?? DEFAULT_COMMITMENT_YEARS,
    discountRatePct: 0,
    reuseAssets: true,
    packages,
    lineItems: [],
    years: Array.from({ length: horizonYears }, () => blankYear(packageIds)),
  };
}

/* ------------------------------------------------------------ group names */

/**
 * The plain-English name of each line-item group.
 *
 * Shared rather than local to the panel that edits them, because the breakdown
 * chart stacks costs by group and a group that is called one thing in the list
 * and another in the legend is two groups as far as the reader is concerned.
 */
export const LINE_GROUP_LABELS: Record<LineGroup, string> = {
  BUILD: "Build",
  INFRASTRUCTURE: "Infrastructure",
  OPERATIONS: "Operations",
  ADDON: "Add-ons",
  SERVICES: "Services",
  OTHER: "Other",
};

/**
 * The rest of the enum labels, here for the same reason: the package card, the
 * diff table and the comparison screen all have to call a `PERPETUAL` package
 * the same thing, and none of them is a better home for the word than this one.
 */
export const BILLING_MODEL_LABELS: Record<BillingModel, string> = {
  SUBSCRIPTION: "Subscription",
  PERPETUAL: "Perpetual",
  HYBRID: "Hybrid",
};

export const MAINTENANCE_MODE_LABELS: Record<MaintenanceMode, string> = {
  NONE: "No maintenance",
  PERCENT_OF_LICENCE: "Percent of the licence fee",
  FLAT: "A flat amount per year",
};

export const LINE_DIRECTION_LABELS: Record<LineDirection, string> = {
  COST: "Cost",
  REVENUE: "Revenue",
};

export const CHURN_MODE_LABELS: Record<ChurnMode, string> = {
  COUNT: "A number of clients",
  RATE: "A percentage of the base",
};

/* ------------------------------------------------------------- basis hints */

export interface BasisMeta {
  label: string;
  /** Names the multiplier, so a wrong basis is visible before the chart shows it. */
  hint: string;
  /** `amount` is a percentage, not money. */
  isPercentage: boolean;
  /** `attachPct` changes the answer for this basis. */
  usesAttachPct: boolean;
  /** `unitsPerClient` changes the answer for this basis. */
  usesUnitsPerClient: boolean;
}

export const BASIS_META: Record<LineBasis, BasisMeta> = {
  FIXED_ONE_TIME: {
    label: "Fixed, one time",
    hint: "Charged once, in its start year only.",
    isPercentage: false,
    usesAttachPct: false,
    usesUnitsPerClient: false,
  },
  FIXED_PER_YEAR: {
    label: "Fixed, per year",
    hint: "The same amount every year in range, whatever the client count.",
    isPercentage: false,
    usesAttachPct: false,
    usesUnitsPerClient: false,
  },
  PER_ACTIVE_CLIENT_YEAR: {
    label: "Per active client, per year",
    hint: "× active clients that year.",
    isPercentage: false,
    usesAttachPct: true,
    usesUnitsPerClient: false,
  },
  PER_NEW_CLIENT_ONCE: {
    label: "Per new client, once",
    hint: "× clients signed that year.",
    isPercentage: false,
    usesAttachPct: true,
    usesUnitsPerClient: false,
  },
  PER_ACTIVE_UNIT_YEAR: {
    label: "Per unit, per year",
    hint: "× active clients × units per client.",
    isPercentage: false,
    usesAttachPct: true,
    usesUnitsPerClient: true,
  },
  PCT_OF_REVENUE: {
    label: "% of revenue",
    hint: "Of that year's revenue before percentage items. Two % items never compound.",
    isPercentage: true,
    usesAttachPct: false,
    usesUnitsPerClient: false,
  },
  PCT_OF_COST: {
    label: "% of cost",
    hint: "Of that year's cost before percentage items. Two % items never compound.",
    isPercentage: true,
    usesAttachPct: false,
    usesUnitsPerClient: false,
  },
};

/* ---------------------------------------------------------------- presets */

/** One-click line items for the costs every product in this shop actually has. */
export const LINE_ITEM_PRESETS: {
  key: string;
  label: string;
  patch: Partial<LineItemInput>;
}[] = [
  {
    key: "initial-build",
    label: "Initial build",
    patch: {
      name: "Initial development",
      direction: "COST",
      group: "BUILD",
      basis: "FIXED_ONE_TIME",
    },
  },
  {
    key: "central-server",
    label: "Central server",
    patch: {
      name: "Central server",
      direction: "COST",
      group: "INFRASTRUCTURE",
      basis: "FIXED_ONE_TIME",
    },
  },
  {
    key: "engineering",
    label: "Engineering & maintenance",
    patch: {
      name: "Development & maintenance",
      direction: "COST",
      group: "BUILD",
      basis: "FIXED_PER_YEAR",
    },
  },
  {
    key: "hosting",
    label: "Hosting",
    patch: {
      name: "Hosting",
      direction: "COST",
      group: "INFRASTRUCTURE",
      basis: "PER_ACTIVE_CLIENT_YEAR",
    },
  },
  // {
  //   key: "onboarding-kit",
  //   label: "Onboarding kit",
  //   patch: {
  //     name: "Onboarding kit",
  //     direction: "COST",
  //     group: "OPERATIONS",
  //     basis: "PER_NEW_CLIENT_ONCE",
  //   },
  // },
  {
    key: "addon-revenue",
    label: "Add-on we sell",
    patch: {
      name: "Add-on",
      direction: "REVENUE",
      group: "ADDON",
      basis: "PER_ACTIVE_CLIENT_YEAR",
    },
  },
  {
    key: "training",
    label: "Training days",
    patch: {
      name: "Training",
      direction: "REVENUE",
      group: "SERVICES",
      basis: "PER_NEW_CLIENT_ONCE",
    },
  },
  // {
  //   key: "commission",
  //   label: "Sales commission",
  //   patch: {
  //     name: "Sales commission",
  //     direction: "COST",
  //     group: "OPERATIONS",
  //     basis: "PCT_OF_REVENUE",
  //   },
  // },
];
